// Custom report builder + saved/scheduled report types.

export type ReportDataset =
  | "EMPLOYEES"
  | "INVOICES"
  | "LEAVE_REQUESTS"
  | "TIME_ENTRIES"
  | "COMPLAINTS";

export type ReportScheduleCadence = "NONE" | "DAILY" | "WEEKLY" | "MONTHLY";

export type ReportExportFormat = "CSV" | "XLSX" | "PDF";

export type ReportOperator =
  | "eq"
  | "ne"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "in"
  | "like"
  | "is_null"
  | "is_not_null";

export type ReportAggregateFn = "count" | "sum" | "avg" | "min" | "max";

// ─────── Builder spec ───────

export interface ReportFilter {
  field: string;
  op: ReportOperator;
  // `value` may be any JSON-serializable primitive or list (for `in`).
  // `is_null` / `is_not_null` ignore the value entirely.
  value?: string | number | boolean | null | (string | number | boolean)[];
}

export interface ReportAggregate {
  fn: ReportAggregateFn;
  // `null` / "*" allowed for count
  field?: string | null;
  as?: string;
}

export interface ReportSortEntry {
  field: string;
  dir: "asc" | "desc";
}

export interface ReportSpec {
  columns?: string[];
  filters?: ReportFilter[];
  aggregates?: ReportAggregate[];
  group_by?: string[];
  sort?: ReportSortEntry[];
  limit?: number;
}

// ─────── Results ───────

export interface ReportRunResult {
  dataset: string;
  columns: string[];
  rows: Array<Array<string | number | boolean | null>>;
  row_count: number;
  limit_applied: number;
  spec?: ReportSpec;
}

// ─────── Dataset catalog (describe_datasets) ───────

export interface DatasetFieldInfo {
  name: string;
  type: "str" | "number" | "bool" | "date" | "datetime" | "uuid" | "enum";
}

export interface DatasetCatalogEntry {
  dataset: ReportDataset;
  fields: DatasetFieldInfo[];
  default_columns: string[];
  operators: ReportOperator[];
  aggregates: ReportAggregateFn[];
}

// ─────── Saved reports ───────

export interface SavedReport {
  id: string;
  name: string;
  description?: string | null;
  dataset: ReportDataset;
  spec_json: ReportSpec;
  is_scheduled: boolean;
  schedule_cadence: ReportScheduleCadence;
  schedule_day_of_week?: number | null;
  schedule_day_of_month?: number | null;
  recipients: string[];
  export_format: ReportExportFormat;
  next_run_at?: string | null;
  last_run_at?: string | null;
  last_run_status?: string | null;
  last_run_error?: string | null;
  created_at: string;
  updated_at: string;
}

export interface RunDueReportsResult {
  ran_at: string;
  delivered: Array<{
    id: string;
    name: string;
    recipients: string[];
    row_count: number;
    filename: string;
    next_run_at: string;
  }>;
  errors: Array<{ id: string; error: string }>;
}

// ─────── Display helpers ───────

export const DATASET_LABELS: Record<ReportDataset, string> = {
  EMPLOYEES: "Employees",
  INVOICES: "Invoices",
  LEAVE_REQUESTS: "Leave requests",
  TIME_ENTRIES: "Time entries",
  COMPLAINTS: "Complaints",
};

export const CADENCE_LABELS: Record<ReportScheduleCadence, string> = {
  NONE: "Manual only",
  DAILY: "Daily",
  WEEKLY: "Weekly",
  MONTHLY: "Monthly",
};

export const FORMAT_LABELS: Record<ReportExportFormat, string> = {
  CSV: "CSV",
  XLSX: "Excel",
  PDF: "PDF",
};

export const OPERATOR_LABELS: Record<ReportOperator, string> = {
  eq: "equals",
  ne: "not equals",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  in: "in",
  like: "contains",
  is_null: "is empty",
  is_not_null: "is not empty",
};
